import type { LiveDebugLogger } from '@/app/[locale]/vidu-stream/live-debug-log';
import { describeError, sanitizeString } from '@/app/[locale]/vidu-stream/live-debug-log';
import type { LiveExitNotice, StoredLiveClaimSession } from '@/app/types';
import {
  isLiveReconnectOnlyReason,
  type LiveCallMediaState,
  type LiveConfigRateLimitAckPayload,
  type LiveErrorPayload,
  type LiveMediaStatusPayload,
  type LiveTextInputAckPayload,
  type LiveTrackStatus,
  type LiveUpdateConfigAckPayload,
  type LiveUpdateConfigPayload,
  normalizeError,
  parseRtcAuthInfo,
  type RemoteVideoBinding,
  resolveLiveExitReasonFromServerCode,
  SIGNAL_TYPE_TIMEOUT_HANGUP,
} from './live-call';
import {
  type LiveChatMessage,
  LiveChatStore,
  MAX_LIVE_CHAT_TEXT_LENGTH,
  truncateLiveChatText,
} from './live-chat-store';
import { LiveReconnector } from './live-reconnector';
import { type AutoPlayBlockedKind, LiveRtcSession } from './live-rtc-session';
import { LiveWsSession, type SendHangupResult, type SendTextInputResult } from './live-ws-session';

const DEFAULT_MAX_CALL_DURATION_SECONDS = 300;
const CONNECT_TIMEOUT_MS = 60_000;
const MEDIA_STATUS_REPORT_INTERVAL_MS = 3_000;
const REMOTE_MEDIA_LOSS_GRACE_MS = 1_500;
/**
 * 唤醒远端画面的去重窗口。
 *
 * `focus` 和 `visibilitychange` 在同一次回前台里都会派发，间隔通常只有几十
 * 毫秒；窗口取 1s 足够把它们合并成一次，又不会影响"切后台再回来"这种真正
 * 需要重新唤醒的场景（那之间至少隔着用户的一次操作）。
 */
const REMOTE_VIEW_RESUME_DEDUPE_MS = 1_000;
const RTC_RECOVERY_GRACE_MS = 20_000;

interface LiveCallSessionState {
  autoPlayBlocked: boolean;
  cameraError: string | null;
  connectionError: string | null;
  elapsedSeconds: number;
  hasEverConnected: boolean;
  isCallReady: boolean;
  isLocalAudioEnabled: boolean;
  isLocalCameraEnabled: boolean;
  isRecovering: boolean;
  isRtcRecovering: boolean;
  isLocalScreenShareEnabled: boolean;
  isScreenShareSupported: boolean;
  isWaitingForNetwork: boolean;
  localAudioError: string | null;
  remainingSeconds: number;
  screenShareError: string | null;
}

interface LiveRtcSessionLike {
  bindLocalView(element: HTMLVideoElement | null): void;
  bindRemoteView(element: HTMLVideoElement | null): void;
  setLocalVideoSource?(source: string | null | undefined): void;
  resumePlayback(): Promise<void> | void;
  resumeRemoteView?(): void;
  start(options: { rtcAuthInfo: ReturnType<typeof parseRtcAuthInfo>; rtcUserId: string; token: string }): Promise<void>;
  stop(): Promise<void>;
  toggleLocalAudio(currentValue: boolean): Promise<void>;
  toggleLocalCamera(currentValue: boolean, cameraPermissionErrorText: string): Promise<void>;
  updateLocalVideoStream(stream: MediaStream | null): Promise<void>;
  toggleLocalScreenShare?(
    currentValue: boolean,
    options: { permissionErrorText: string; startErrorText: string; unsupportedErrorText: string },
  ): Promise<void>;
  restoreLocalMedia?(): Promise<void>;
}

interface LiveWsSessionLike {
  close(options?: { hangupReason?: string; sendHangup?: boolean }): SendHangupResult | null;
  connect(): void;
  dispose(): void;
  sendMediaStatus?(options: { payload: LiveMediaStatusPayload }): boolean;
  sendUpdateLiveConfig?(options: { payload: LiveUpdateConfigPayload }): boolean;
  sendTextInput?(options: { clientMsgId: string; text: string }): SendTextInputResult;
}

interface RemoteMediaSnapshot {
  audioBytesReceived?: number;
  audioPacketsReceived?: number;
  hasAudioTrack?: boolean;
  hasVideoTrack?: boolean;
  sampledAtMs: number;
  subscribeLoss?: number;
  subscribeQuality?: number;
  subscribeRttMs?: number;
  uid: string | null;
  videoBytesReceived?: number;
  videoDecodeFps?: number;
  videoFramesDecoded?: number;
  videoFramesReceived?: number;
  videoFramesRendered?: number;
  videoFrameHeight?: number;
  videoFrameWidth?: number;
  videoPacketsReceived?: number;
}

const mergeSnapshotValue = <T>(nextValue: T | undefined, previousValue: T, fallbackValue: T): T => {
  if (nextValue !== undefined) {
    return nextValue;
  }

  return previousValue ?? fallbackValue;
};

interface CreateLiveCallSessionParams {
  audioEnabled?: boolean;
  deferLocalAudioInitialization?: boolean;
  localVideoStream?: MediaStream | null;
  localVideoSource?: string | null;
  appCloseConnNoticeText: string;
  cameraPermissionErrorText: string;
  connectionErrorText: string;
  rtcUnsupportedErrorText?: string;
  liveId: string;
  /** 从 use-live-call 传入的通话链路 logger；LiveCallSession 转发给 WS/RTC/Reconnector 共用。 */
  logger?: LiveDebugLogger;
  onChatMessagesChange?: (messages: LiveChatMessage[]) => void;
  onTextModerationRejected?: () => void;
  onLiveConfigRateLimit?: (payload: LiveConfigRateLimitAckPayload) => void;
  onUpdateLiveConfigAck?: (payload: LiveUpdateConfigAckPayload) => void;
  onExit: (notice?: LiveExitNotice) => void | Promise<void>;
  onRouteLeave?: (payload: { didTrigger: boolean; liveId: string; reason: LiveSessionLeaveReason }) => void;
  onStateChange?: (state: LiveCallSessionState) => void;
  rtcSessionFactory?: (params: ConstructorParameters<typeof LiveRtcSession>[0]) => LiveRtcSessionLike;
  screenShareStartErrorText: string;
  screenShareUnsupportedErrorText: string;
  session: StoredLiveClaimSession;
  wsSessionFactory?: (params: ConstructorParameters<typeof LiveWsSession>[0]) => LiveWsSessionLike;
}

type TimerId = ReturnType<typeof setTimeout>;
type IntervalId = ReturnType<typeof setInterval>;
export type LiveSessionLeaveReason = 'route_change_leave' | 'pagehide_leave';
type ConnectionErrorSource = 'rtc' | 'timeout' | 'unknown' | 'ws';

interface ActiveLiveCallSession {
  liveId: string;
  session: Pick<LiveCallSession, 'dispose' | 'handleRouteLeave'>;
}

interface ActiveLiveCallLeaveOptions {
  liveId: string;
  reason: LiveSessionLeaveReason;
}

interface ActiveLiveCallUnmountOptions {
  liveId: string;
  onDispose?: () => Promise<void> | void;
  session: Pick<LiveCallSession, 'dispose' | 'handleRouteLeave'>;
}

const LIVE_DETAIL_PREFIX = '/vidu-stream/';

class ActiveLiveCallCoordinator {
  private activeSession: ActiveLiveCallSession | null = null;
  private pendingUnmountDispose: (ActiveLiveCallUnmountOptions & { timerId: TimerId }) | null = null;

  register({ liveId, session }: ActiveLiveCallSession): void {
    this.activeSession = {
      liveId,
      session,
    };
  }

  clear(liveId?: string): void {
    if (!this.activeSession) {
      return;
    }

    if (!liveId || this.activeSession.liveId === liveId) {
      this.activeSession = null;
    }
  }

  triggerLeave({ liveId, reason }: ActiveLiveCallLeaveOptions): boolean {
    if (!this.activeSession || this.activeSession.liveId !== liveId) {
      return false;
    }

    this.activeSession.session.handleRouteLeave(reason);
    return true;
  }

  scheduleUnmountDispose({ liveId, onDispose, session }: ActiveLiveCallUnmountOptions): void {
    this.cancelPendingUnmountDispose();

    const timerId = setTimeout(() => {
      if (this.pendingUnmountDispose?.timerId === timerId) {
        this.pendingUnmountDispose = null;
      }

      if (this.activeSession?.liveId === liveId && this.activeSession.session === session) {
        this.clear(liveId);
      }

      void (async () => {
        await onDispose?.();
        await session.dispose();
      })();
    }, 0);
    this.pendingUnmountDispose = { liveId, onDispose, session, timerId };
  }

  cancelPendingUnmountDispose(): void {
    const pending = this.pendingUnmountDispose;
    if (!pending) return;

    clearTimeout(pending.timerId);
    this.pendingUnmountDispose = null;
    void (async () => {
      await pending.onDispose?.();
      await pending.session.dispose();
    })();
  }
}

const activeLiveCallCoordinator = new ActiveLiveCallCoordinator();

export const getLiveDetailIdFromPathname = (pathname: string | null | undefined): string | null => {
  if (!pathname || !pathname.startsWith(LIVE_DETAIL_PREFIX)) {
    return null;
  }

  const liveId = pathname.slice(LIVE_DETAIL_PREFIX.length).split('/')[0]?.trim() ?? '';

  if (!liveId || liveId === 'live' || liveId === 'editing' || pathname !== `${LIVE_DETAIL_PREFIX}${liveId}`) {
    return null;
  }

  return liveId;
};

export const registerActiveLiveCallSession = (entry: ActiveLiveCallSession): void => {
  activeLiveCallCoordinator.register(entry);
};

export const clearActiveLiveCallSession = (liveId?: string): void => {
  activeLiveCallCoordinator.clear(liveId);
};

export const triggerActiveLiveCallSessionLeave = (options: ActiveLiveCallLeaveOptions): boolean => {
  return activeLiveCallCoordinator.triggerLeave(options);
};

export const scheduleActiveLiveCallSessionUnmountDispose = (options: ActiveLiveCallUnmountOptions): void => {
  activeLiveCallCoordinator.scheduleUnmountDispose(options);
};

export const cancelActiveLiveCallSessionUnmountDispose = (): void => {
  activeLiveCallCoordinator.cancelPendingUnmountDispose();
};

const hasNetworkAccess = (): boolean => {
  if (typeof navigator === 'undefined' || typeof navigator.onLine !== 'boolean') {
    return true;
  }

  return navigator.onLine;
};

export class LiveCallSession {
  private readonly appCloseConnNoticeText: string;
  private readonly cameraPermissionErrorText: string;
  private readonly connectionErrorText: string;
  private readonly rtcUnsupportedErrorText: string;
  private readonly liveId: string;
  private readonly logger?: LiveDebugLogger;
  private readonly chatStore: LiveChatStore;
  private readonly onExit: CreateLiveCallSessionParams['onExit'];
  private readonly onRouteLeave?: CreateLiveCallSessionParams['onRouteLeave'];
  private readonly onTextModerationRejected?: CreateLiveCallSessionParams['onTextModerationRejected'];
  private readonly onLiveConfigRateLimit?: CreateLiveCallSessionParams['onLiveConfigRateLimit'];
  private readonly onUpdateLiveConfigAck?: CreateLiveCallSessionParams['onUpdateLiveConfigAck'];
  private readonly onStateChange?: CreateLiveCallSessionParams['onStateChange'];
  private readonly rtcSessionFactory: NonNullable<CreateLiveCallSessionParams['rtcSessionFactory']>;
  private readonly screenShareStartErrorText: string;
  private readonly screenShareUnsupportedErrorText: string;
  private readonly session: StoredLiveClaimSession;
  private readonly wsSessionFactory: NonNullable<CreateLiveCallSessionParams['wsSessionFactory']>;
  private hasConnInitNotReady = false;

  private readonly rtcSession: LiveRtcSessionLike;
  private readonly reconnector: LiveReconnector;

  private callStartedAt: number | null = null;
  private autoPlayBlockedKinds = new Set<AutoPlayBlockedKind>();
  /** 最近一次 3s 快照算出的音视频流状态，供 autoplay 相关日志带上当时的实况 */
  private lastAudioStatus: LiveTrackStatus | null = null;
  private lastVideoStatus: LiveTrackStatus | null = null;
  /** 「提示还挂着但音频包已经在流动」只在状态跳变时报一次，避免 3s 一条刷爆 */
  private hasLoggedAutoPlayStalePrompt = false;
  private connectTimeoutTimer: TimerId | null = null;
  private disposed = false;
  private exited = false;
  private hasEverRenderedRemoteFrame = false;
  private hasRemoteFrame = false;
  /** 上次唤醒远端画面的时刻，用于合并 focus + visibilitychange 的重复触发 */
  private lastRemoteViewResumeAt = 0;
  private hasStartedRtc = false;
  private isRebuildingRtc = false;
  private lastCallMediaState: LiveCallMediaState | null = null;
  private lastExitReason = '';
  private localVideoElement: HTMLVideoElement | null = null;
  private remoteBindings: RemoteVideoBinding[] = [];
  private remoteFrameCleanup: (() => void) | null = null;
  private remoteMediaSnapshot: RemoteMediaSnapshot | null = null;
  private remoteMediaStatsTimer: TimerId | null = null;
  private remoteMediaLossTimer: TimerId | null = null;
  private rtcRecoveryTimer: TimerId | null = null;
  private retryRtcRebuildAfterFailure = false;
  private remoteVideoElement: HTMLVideoElement | null = null;
  private routeLeaveInFlight = false;
  private state: LiveCallSessionState;
  private tickTimer: IntervalId | null = null;
  private wsSession: LiveWsSessionLike | null = null;

  /**
   * 决定「自动播放被拦截」提示是否显示。
   *
   * video 侧信任 SDK 的拦截事件：它在 emit 前已经排除了 AbortError 这类无害
   * 的中断（`a.code !== 20 && a.name !== "AbortError"`），事件本身可靠，且恢复
   * 有首帧渲染作为明确信号。
   *
   * audio 侧不能只信事件，必须等到「确实测到没有音频包」才显示。原因有两个：
   *   1. SDK 的音频路径是裸 catch，AbortError 也会当成拦截上报，误报率天然更高
   *   2. SDK 在 emit 后自己挂 click/touchstart 调 resumeAudio() 静默恢复，恢复
   *      成功不发任何事件——即事件为真也可能早已自愈
   * 所以判据取 `track_only`（有音轨但 3s 内没有包）。`flowing` 说明声音在走，
   * `null` 说明还没测到、证据不足——两种都不显示，宁可晚报也不要闪一下。
   *
   * 这样排序也顺带消掉了「提示先出现再消失」：首帧渲染那一刻若音频已在流动，
   * 提示根本不会出现，而不是出现后等下一个 3s 快照才收起。
   */
  private syncAutoPlayBlockedState(hasRenderedVideo = this.hasEverRenderedRemoteFrame): void {
    const audioMeasuredSilent = this.autoPlayBlockedKinds.has('audio') && this.lastAudioStatus === 'track_only';
    const shouldShow = this.autoPlayBlockedKinds.has('video') || (hasRenderedVideo && audioMeasuredSilent);
    if (this.state.autoPlayBlocked !== shouldShow) {
      this.setState({ autoPlayBlocked: shouldShow });
    }
  }

  private clearAutoPlayBlockedState(): void {
    this.autoPlayBlockedKinds.clear();
    this.hasLoggedAutoPlayStalePrompt = false;
    // 重建后媒体统计会从头累积，旧的 track_only 不能继续参与显示判断
    this.lastAudioStatus = null;
    if (this.state.autoPlayBlocked) {
      this.setState({ autoPlayBlocked: false });
    }
  }

  /**
   * 音频包恢复流动后自动收起「自动播放被拦截」提示。
   *
   * 为什么需要主动收：aliyun-rtc-sdk 7.3.3 的 `handleAutoPlayFailed` 在 emit
   * `remoteAudioAutoPlayFail` 之后，会自己在 document 上挂 click / touchstart
   * 监听，用户随便碰一下屏幕它就调 `resumeAudio()` 把声音救回来——但恢复成功
   * **不发任何事件**。所以「声音已经恢复」这件事没有任何回调能告诉我们，提示
   * 就一直挂着。华为 WebView 上尤其容易触发（Android 对音频自动播放的策略比
   * 视频严），实测 audio_status=flowing、audio_packets_delta_3s=180 时提示仍在。
   *
   * 判据用「音频包在 3s 内有增量」（audio_status === 'flowing'）。严格说它证明
   * 的是"收到了音频包"，不等于"扬声器确实出声"；但 SDK 的 <audio> 元素由它自己
   * 创建并 autoplay，包在流 + 元素没被暂停基本等价于在播。这和 video 侧用首帧
   * 渲染自动摘掉 'video' 是同一种对称做法（见 markRendered）。
   *
   * 日志只在首次命中时打一条：3s 一条会把一次通话刷成几十条同样的记录。但**收起
   * 提示本身不受该去重标记限制**——音频若二次被拦，恢复后仍应再次自动收起。
   */
  private reconcileAutoPlayBlockedWithAudioFlow({
    audioStatus,
    videoStatus,
    audioPacketsDelta3s,
  }: {
    audioStatus: LiveTrackStatus;
    videoStatus: LiveTrackStatus;
    audioPacketsDelta3s: number;
  }): void {
    if (!this.autoPlayBlockedKinds.has('audio')) {
      return;
    }

    /*
     * 音频包在流动 → SDK 已经自行恢复了播放，彻底摘掉 audio。
     *
     * 不只是隐藏提示：留在集合里的话，后续某个 3s 窗口恰好没有包（网络抖动、
     * 说话间隙）就会让提示重新冒出来，那正是「闪一下」的另一种形态。
     */
    if (audioStatus === 'flowing') {
      if (!this.hasLoggedAutoPlayStalePrompt && this.state.autoPlayBlocked) {
        this.hasLoggedAutoPlayStalePrompt = true;
        this.logger?.log('rtc', 'autoplay_prompt_stale', {
          audio_status: audioStatus,
          video_status: videoStatus,
          audio_packets_delta_3s: audioPacketsDelta3s,
          blocked_kinds: [...this.autoPlayBlockedKinds].sort().join(','),
          has_rendered_video: this.hasEverRenderedRemoteFrame,
          elapsed_ms_since_session_start: this.logger.since('session_start'),
        });
      }

      this.autoPlayBlockedKinds.delete('audio');
    }

    // audioStatus 变化会改变提示的可见性（见 syncAutoPlayBlockedState），
    // 所以每个快照都要重算一次，而不只在摘掉 audio 时。
    this.syncAutoPlayBlockedState();
  }

  constructor({
    appCloseConnNoticeText,
    audioEnabled,
    deferLocalAudioInitialization,
    localVideoStream,
    localVideoSource,
    cameraPermissionErrorText,
    connectionErrorText,
    rtcUnsupportedErrorText,
    liveId,
    logger,
    onChatMessagesChange,
    onTextModerationRejected,
    onLiveConfigRateLimit,
    onUpdateLiveConfigAck,
    onExit,
    onRouteLeave,
    onStateChange,
    rtcSessionFactory = (params) => new LiveRtcSession(params),
    screenShareStartErrorText,
    screenShareUnsupportedErrorText,
    session,
    wsSessionFactory = (params) => new LiveWsSession(params),
  }: CreateLiveCallSessionParams) {
    this.appCloseConnNoticeText = appCloseConnNoticeText;
    this.cameraPermissionErrorText = cameraPermissionErrorText;
    this.connectionErrorText = connectionErrorText;
    this.rtcUnsupportedErrorText = rtcUnsupportedErrorText ?? connectionErrorText;
    this.liveId = liveId;
    this.logger = logger;
    this.chatStore = new LiveChatStore((messages) => {
      onChatMessagesChange?.(messages);
    });
    this.onExit = onExit;
    this.onRouteLeave = onRouteLeave;
    this.onTextModerationRejected = onTextModerationRejected;
    this.onLiveConfigRateLimit = onLiveConfigRateLimit;
    this.onUpdateLiveConfigAck = onUpdateLiveConfigAck;
    this.onStateChange = onStateChange;
    this.rtcSessionFactory = rtcSessionFactory;
    this.screenShareStartErrorText = screenShareStartErrorText;
    this.screenShareUnsupportedErrorText = screenShareUnsupportedErrorText;
    this.session = session;
    this.wsSessionFactory = wsSessionFactory;
    this.state = {
      autoPlayBlocked: false,
      cameraError: null,
      connectionError: null,
      elapsedSeconds: 0,
      hasEverConnected: false,
      isCallReady: false,
      isLocalAudioEnabled: audioEnabled !== false,
      isLocalCameraEnabled: false,
      isRecovering: false,
      isLocalScreenShareEnabled: false,
      isRtcRecovering: false,
      isScreenShareSupported: false,
      isWaitingForNetwork: false,
      localAudioError: null,
      remainingSeconds: this.getMaxDurationSeconds(),
      screenShareError: null,
    };

    this.rtcSession = this.rtcSessionFactory({
      audioEnabled,
      deferLocalAudioInitialization,
      localVideoStream,
      localVideoSource,
      logger: this.logger,
      onAutoPlayBlocked: (kind) => {
        const isNewKind = !this.autoPlayBlockedKinds.has(kind);
        this.autoPlayBlockedKinds.add(kind);
        // 音频二次被拦时重新允许打一条 stale 取证，否则只能看到第一次的记录
        if (kind === 'audio' && isNewKind) {
          this.hasLoggedAutoPlayStalePrompt = false;
        }
        /*
         * 记下"被拦截"这一刻的媒体实况。
         *
         * 排查「声音视频都正常但提示还在」时，光知道事件发生过没用——要知道
         * 事件发生时数据流到底在不在。SDK 只在失败时给回调、恢复后不给任何
         * 信号，所以恢复只能靠后续快照里 audio_status 的变化反推。
         */
        this.logger?.log('rtc', 'autoplay_blocked_raised', {
          kind,
          audio_status: this.lastAudioStatus ?? undefined,
          video_status: this.lastVideoStatus ?? undefined,
          has_rendered_video: this.hasEverRenderedRemoteFrame,
          is_call_ready: this.state.isCallReady,
          blocked_kinds: [...this.autoPlayBlockedKinds].sort().join(','),
          elapsed_ms_since_session_start: this.logger.since('session_start'),
        });
        this.syncAutoPlayBlockedState();
      },
      onCameraError: (message) => {
        this.setState({ cameraError: message });
      },
      onConnectionEstablished: () => {
        this.clearRtcRecoveryTimer();

        if (this.hasEverRenderedRemoteFrame) {
          void this.restoreLocalMedia();
          this.setState({
            connectionError: null,
            isCallReady: true,
            isRecovering: false,
            isRtcRecovering: false,
            isWaitingForNetwork: false,
          });
          this.rtcSession.bindRemoteView(this.remoteVideoElement);
          return;
        }

        this.setState({ connectionError: null });
      },
      onConnectionRecovering: () => {
        this.scheduleRtcRecovery();
      },
      onConnectionFailure: () => {
        if (this.state.hasEverConnected) {
          if (this.isRebuildingRtc) {
            // LiveRtcSession.start handles its own errors, so defer the retry until rebuild unlocks.
            this.hasStartedRtc = false;
            this.retryRtcRebuildAfterFailure = true;
            return;
          }

          this.scheduleRtcRecovery();
          return;
        }

        this.showConnectionError(this.state.connectionError || this.connectionErrorText, {
          phase: 'rtc_status_failed',
          source: 'rtc',
        });
      },
      onRtcUnsupported: () => {
        this.showConnectionError(this.rtcUnsupportedErrorText, {
          phase: 'rtc_unsupported',
          source: 'rtc',
        });
      },
      onLocalAudioEnabledChange: (enabled) => {
        this.setState({ isLocalAudioEnabled: enabled, localAudioError: null });
      },
      onLocalAudioRestoreError: (message) => {
        this.setState({ localAudioError: message });
      },
      onLocalCameraEnabledChange: (enabled) => {
        this.setState({ isLocalCameraEnabled: enabled });
      },
      onLocalScreenShareActiveChange: (active) => {
        this.setState({ isLocalScreenShareEnabled: active });
      },
      onRemoteMediaStatsChange: (snapshot) => {
        this.handleRemoteMediaStatsChange(snapshot);
      },
      onRemoteTrackPresenceChange: (presence) => {
        this.handleRemoteTrackPresenceChange(presence);
      },
      onRemoteBindingsChange: (bindings) => {
        this.handleRemoteBindingsChange(bindings);
      },
      onScreenShareError: (message) => {
        this.setState({ screenShareError: message });
      },
      onScreenShareSupportedChange: (supported) => {
        this.setState({ isScreenShareSupported: supported });
      },
    });

    this.reconnector = new LiveReconnector({
      clearTimer: (timerId) => {
        window.clearTimeout(timerId);
      },
      hasNetworkAccess,
      logger: this.logger,
      onGiveUp: (message) => {
        this.showConnectionError(message || this.connectionErrorText, {
          phase: 'reconnect_give_up',
          source: 'ws',
        });
      },
      onReconnect: () => {
        void this.reconnect();
      },
      onRecovering: (message) => {
        this.setState({
          connectionError: message || null,
          isWaitingForNetwork: false,
        });
      },
      onWaitingForNetwork: (message) => {
        this.setState({
          connectionError: message || null,
          isWaitingForNetwork: true,
        });
      },
      retryForever: true,
      setTimer: (callback, delayMs) => setTimeout(callback, delayMs),
    });
  }

  start(): void {
    this.logger?.markOnce('session_start');
    this.logger?.log('call', 'session_start', {
      max_duration_seconds: this.getMaxDurationSeconds(),
      elapsed_ms_since_claim_request_start: this.logger.since('claim_request_start'),
      elapsed_ms_since_claim_success: this.logger.since('claim_success'),
    });
    this.emitState();
    this.attachWindowListeners();
    this.startConnectTimeout();
    this.startWsSession();
  }

  bindRemoteVideo(element: HTMLVideoElement | null): void {
    this.remoteVideoElement = element;
    this.rtcSession.bindRemoteView(element);
    this.rebindRemoteFrameDetector();
  }

  bindLocalVideo(element: HTMLVideoElement | null): void {
    this.localVideoElement = element;
    this.rtcSession.bindLocalView(element);
  }

  async toggleLocalAudio(): Promise<void> {
    await this.rtcSession.toggleLocalAudio(this.state.isLocalAudioEnabled);
  }

  async toggleLocalCamera(): Promise<void> {
    await this.rtcSession.toggleLocalCamera(this.state.isLocalCameraEnabled, this.cameraPermissionErrorText);
  }

  async updateLocalVideoStream(stream: MediaStream | null): Promise<void> {
    await this.rtcSession.updateLocalVideoStream(stream);
  }

  setLocalVideoSource(source: string | null | undefined): void {
    this.rtcSession.setLocalVideoSource?.(source);
  }

  /**
   * 发送 update_live_config（16）。client_msg_id 在这里生成，调用方不用关心。
   */
  updateLiveConfig(config: import('./live-call').LiveEditingConfig): boolean {
    const payload: LiveUpdateConfigPayload = {
      client_msg_id: this.generateClientMsgId(),
      config: { editing: config },
    };
    return this.wsSession?.sendUpdateLiveConfig?.({ payload }) ?? false;
  }

  updateAvatarLiveConfig(
    config: LiveUpdateConfigPayload['config'] extends infer _T
      ? { scene?: string; references: import('./live-call').LiveAvatarReferenceInput[] }
      : never,
  ): string | null {
    if (!this.wsSession?.sendUpdateLiveConfig) return null;
    const clientMsgId = this.generateClientMsgId();
    const payload: LiveUpdateConfigPayload = {
      client_msg_id: clientMsgId,
      config: { avatar: config },
    };
    this.wsSession.sendUpdateLiveConfig({ payload });
    return clientMsgId;
  }

  clearLiveConfig(): string | null {
    if (!this.wsSession?.sendUpdateLiveConfig) return null;
    const clientMsgId = this.generateClientMsgId();
    this.wsSession.sendUpdateLiveConfig({
      payload: { client_msg_id: clientMsgId, config: { clear: {} } },
    });
    return clientMsgId;
  }

  removeLatestReference(): string | null {
    if (!this.wsSession?.sendUpdateLiveConfig) return null;
    const clientMsgId = this.generateClientMsgId();
    this.wsSession.sendUpdateLiveConfig({
      payload: { client_msg_id: clientMsgId, config: { remove_latest_reference: {} } },
    });
    return clientMsgId;
  }

  async toggleLocalScreenShare(): Promise<void> {
    if (!this.rtcSession.toggleLocalScreenShare) {
      return;
    }
    const currentlySharing = this.state.isLocalScreenShareEnabled;
    await this.rtcSession.toggleLocalScreenShare(currentlySharing, {
      permissionErrorText: this.cameraPermissionErrorText,
      startErrorText: this.screenShareStartErrorText,
      unsupportedErrorText: this.screenShareUnsupportedErrorText,
    });
  }

  /**
   * 发送用户文本：乐观渲染 pending 气泡后经 WS 发出。
   * WS 非 OPEN（重连空窗）时不静默丢：立即把该消息置为 failed，供 UI 展示重发。
   * 成功投递（socket OPEN）视为 sent；服务端后续可通过 error(msg_id) 再收口为 failed。
   * 返回本次使用的 clientMsgId（重发时可复用）。
   */
  sendText(text: string, type: LiveChatMessage['type'] = 'chat'): string | null {
    const trimmed = truncateLiveChatText(text.trim(), MAX_LIVE_CHAT_TEXT_LENGTH);
    if (!trimmed) {
      return null;
    }

    const clientMsgId = this.generateClientMsgId();
    this.chatStore.addPendingUserMessage(clientMsgId, trimmed, type);
    this.dispatchTextInput(clientMsgId, trimmed);
    return clientMsgId;
  }

  /** 重发一条 failed 用户消息：复用原 clientMsgId，重置为 pending 后重新发送。 */
  resendText(clientMsgId: string, text: string): void {
    const trimmed = truncateLiveChatText(text.trim(), MAX_LIVE_CHAT_TEXT_LENGTH);
    if (!trimmed) {
      return;
    }

    this.chatStore.setUserMessageStatus(clientMsgId, 'pending');
    this.dispatchTextInput(clientMsgId, trimmed);
  }

  /** 气泡渐隐结束后从列表移除（sent 的用户气泡几秒后消失；failed 的不移除，留作重发）。 */
  removeChatMessage(clientMsgId: string): void {
    this.chatStore.removeMessage(clientMsgId);
  }

  endCall(): void {
    this.lastExitReason = 'user_press_endcall';
    void this.exitFromClientActionOrFallback();
  }

  resumePlayback(): void {
    /*
     * 带上点击时的实况：如果这里 audio_status 已经是 flowing，说明用户是在
     * 声音本来就正常的情况下被迫点了这个按钮——即提示是误报。这条和
     * autoplay_prompt_stale 互为印证，后者是被动采样、这条是用户主动行为。
     */
    this.logger?.log('rtc', 'autoplay_resume_clicked', {
      blocked_kinds: [...this.autoPlayBlockedKinds].sort().join(','),
      audio_status: this.lastAudioStatus ?? undefined,
      video_status: this.lastVideoStatus ?? undefined,
      has_rendered_video: this.hasEverRenderedRemoteFrame,
      elapsed_ms_since_session_start: this.logger.since('session_start'),
    });

    this.autoPlayBlockedKinds.clear();
    this.hasLoggedAutoPlayStalePrompt = false;
    this.setState({ autoPlayBlocked: false });
    const playback = this.rtcSession.resumePlayback();

    if (playback && typeof playback.then === 'function') {
      void playback.catch(() => {
        this.autoPlayBlockedKinds.add('video');
        this.syncAutoPlayBlockedState();
      });
    }
  }

  handleRouteLeave(reason: LiveSessionLeaveReason): void {
    if (this.exited || this.routeLeaveInFlight) {
      return;
    }

    this.logger?.log('call', 'session_route_leave', {
      reason,
      has_ever_connected: this.state.hasEverConnected,
      elapsed_ms_since_session_start: this.logger.since('session_start'),
    });
    // handleRouteLeave 走独立清理路径不经过 exitWithTransportCleanup，也补一次 session_exit，
    // 保证 route/pagehide 场景在退出事件里能被查到（reason=route_change_leave / pagehide_leave）。
    // exited 前置守卫已经防止其他路径先跑过来重复上报。
    this.logger?.logOnce('session_exit', 'call', 'session_exit', {
      reason,
      has_ever_connected: this.state.hasEverConnected,
      elapsed_ms_since_claim_request_start: this.logger.since('claim_request_start'),
      elapsed_ms_since_claim_success: this.logger.since('claim_success'),
      elapsed_ms_since_session_start: this.logger.since('session_start'),
      send_hangup: true,
      via: 'route_leave',
    });

    this.routeLeaveInFlight = true;
    this.exited = true;
    this.detachWindowListeners();
    this.clearConnectTimeout();
    this.clearRemoteMediaStatsTimer();
    this.clearTickTimer();
    this.clearRemoteMediaLossTimer();
    this.clearRtcRecoveryTimer();
    this.reconnector.stop();
    this.lastExitReason = reason;
    this.closeWsSession(true, reason);

    this.onRouteLeave?.({
      didTrigger: true,
      liveId: this.liveId,
      reason,
    });

    void this.disposeTransport();
  }

  handleOnline(): void {
    if (this.exited || this.disposed) {
      return;
    }

    this.clearRemoteMediaLossTimer();
    this.reconnector.handleOnline();
    if (this.state.isRtcRecovering) {
      this.scheduleRtcRecovery();
    }
  }

  handleOffline(): void {
    if (this.exited || this.disposed) {
      return;
    }

    if (!this.state.hasEverConnected && !this.state.isRecovering) {
      return;
    }

    this.clearRemoteMediaLossTimer();
    this.clearRtcRecoveryTimer();
    this.reconnector.handleOffline();
  }

  handleForegroundReconnect(): void {
    if (this.exited || this.disposed || document.visibilityState !== 'visible') {
      return;
    }

    /*
     * 连接还活着（isCallReady）时不需要重连，但**画面可能需要唤醒**。
     *
     * iOS WebView 切后台会暂停 video 解码，回前台不自己恢复；而 socket 和 RTC
     * 都没断，所以下面那些守卫会全部命中、直接 return —— 这正是「切后台回来
     * 画面一直黑、但还能和后端正常交互」的原因。Android 会自动续播，所以只有
     * iOS 有这个现象。
     *
     * 所以先无条件尝试唤醒画面（幂等、不重建连接），再判断要不要真的重连。
     *
     * 去重是必要的：`focus` 和 `visibilitychange` 都绑到本方法，iOS 回前台时
     * 两个事件都会派发，唤醒就会跑两次 —— 表现为画面闪两下。同一次回前台只
     * 需要唤醒一次，所以用时间窗把紧邻的重复调用合并掉。
     */
    const now = Date.now();
    if (now - this.lastRemoteViewResumeAt > REMOTE_VIEW_RESUME_DEDUPE_MS) {
      this.lastRemoteViewResumeAt = now;
      this.rtcSession.resumeRemoteView?.();
    }

    if (
      !this.state.hasEverConnected ||
      this.state.isCallReady ||
      this.state.isWaitingForNetwork ||
      this.reconnector.hasPendingReconnect()
    ) {
      return;
    }

    void this.reconnect();
  }

  async dispose(): Promise<void> {
    if (this.disposed) {
      return;
    }

    this.disposed = true;
    this.detachWindowListeners();
    this.clearConnectTimeout();
    this.clearRemoteMediaStatsTimer();
    this.clearTickTimer();
    this.clearRemoteMediaLossTimer();
    this.clearRtcRecoveryTimer();
    this.reconnector.stop();
    this.cleanupRemoteFrameDetector();
    await this.disposeTransport();
  }

  private async reconnect(): Promise<void> {
    if (this.exited || this.disposed) {
      return;
    }

    this.closeWsSession(false);

    this.setState({
      connectionError: null,
      isWaitingForNetwork: false,
    });
    this.startWsSession();
  }

  private startWsSession(): void {
    if (this.exited || this.disposed) {
      return;
    }

    try {
      const wsSession = this.wsSessionFactory({
        accessToken: this.session.transportAccessToken,
        liveId: this.liveId,
        logger: this.logger,
        onConnInitAckFailure: ({ errorCode, errorMessage }) => {
          const mappedReason = resolveLiveExitReasonFromServerCode(errorCode);
          const reason = mappedReason ?? (this.hasConnInitNotReady ? 'not_ready' : 'connect_timeout');

          this.showConnectionError(errorMessage || this.connectionErrorText, {
            error_code: errorCode,
            phase: 'ws_init_ack',
            source: 'ws',
          });

          void this.exitFromServerSignal({
            notice: mappedReason ? { reason: mappedReason } : { reason, message: errorMessage || undefined },
          });
        },
        onConnInitAckNotReady: () => {
          this.hasConnInitNotReady = true;
        },
        onConnInitAckReady: () => {
          if (this.exited || this.disposed) {
            return;
          }

          if (this.hasStartedRtc) {
            if (this.state.hasEverConnected) {
              this.reconnector.notifySuccess();
            }

            this.setState({
              connectionError: null,
              isWaitingForNetwork: false,
            });
            return;
          }

          if (this.state.hasEverConnected) {
            this.reconnector.notifySuccess();
          }

          this.setState({ connectionError: null, isWaitingForNetwork: false });
          void this.startRtcSession();
        },
        onConnectionClosed: () => {
          if (this.exited || this.disposed) {
            return;
          }

          if (this.state.hasEverConnected) {
            this.scheduleWsReconnect();
          }
        },
        onConnectionError: () => {
          if (this.exited || this.disposed) {
            return;
          }

          if (this.state.hasEverConnected) {
            this.scheduleWsReconnect();
            return;
          }

          this.showConnectionError(this.state.connectionError || this.connectionErrorText, {
            phase: 'ws_error',
            source: 'ws',
          });
        },
        onConnectionOpen: () => {
          this.setState({ connectionError: null });
        },
        onServerForceHangup: ({ rawReason, signalType }) => {
          void this.handleServerDrivenTerminationSignal(signalType, rawReason);
        },
        onTextInputAck: (payload: LiveTextInputAckPayload) => {
          this.chatStore.applyAck(payload);
        },
        onServerError: (payload: LiveErrorPayload) => {
          if (payload.code?.trim() === 'TEXT_INPUT_SENSITIVE') {
            const msgId = payload.msg_id?.trim();
            if (msgId) {
              this.chatStore.removeMessage(msgId);
            }
            this.onTextModerationRejected?.();
            return;
          }
          this.chatStore.applyError(payload);
        },
        onLiveConfigRateLimit: (payload) => this.onLiveConfigRateLimit?.(payload),
        onUpdateLiveConfigAck: (payload) => this.onUpdateLiveConfigAck?.(payload),
        rtcUserId: this.session.claim.rtc.user_id,
      });

      this.wsSession = wsSession;
      wsSession.connect();
      this.startRemoteMediaStatsTimer();
    } catch (error) {
      const message = normalizeError(error);

      if (this.state.hasEverConnected) {
        this.scheduleWsReconnect(message);
        return;
      }

      this.showConnectionError(message, {
        phase: 'ws_connect',
        source: 'ws',
      });
    }
  }

  private async startRtcSession(): Promise<void> {
    if (this.hasStartedRtc || this.exited || this.disposed) {
      return;
    }

    this.hasStartedRtc = true;
    const rtcAuthInfo = parseRtcAuthInfo(this.session.claim.rtc);

    await this.rtcSession.start({
      rtcAuthInfo,
      rtcUserId: this.session.claim.rtc.user_id,
      token: this.session.claim.rtc.token,
    });

    this.rtcSession.bindLocalView(this.localVideoElement);
    this.rtcSession.bindRemoteView(this.remoteVideoElement);
  }

  private async restoreLocalMedia(): Promise<void> {
    try {
      await this.rtcSession.restoreLocalMedia?.();
    } catch {
      // Local media recovery is best effort and must not interrupt remote playback.
    }
  }

  private handleRemoteBindingsChange(bindings: RemoteVideoBinding[]): void {
    this.remoteBindings = bindings;
    this.rtcSession.bindRemoteView(this.remoteVideoElement);

    if (bindings.length === 0) {
      if (this.hasEverRenderedRemoteFrame) {
        this.scheduleRemoteMediaLossRecovery();
      }
      return;
    }

    this.clearRemoteMediaLossTimer();

    this.rebindRemoteFrameDetector();
  }

  private handleRemoteTrackPresenceChange(presence: {
    hasAudioTrack: boolean;
    hasVideoTrack: boolean;
    uid: string;
  }): void {
    const previous = this.remoteMediaSnapshot;

    this.remoteMediaSnapshot = {
      audioBytesReceived: previous?.audioBytesReceived ?? 0,
      audioPacketsReceived: previous?.audioPacketsReceived ?? 0,
      hasAudioTrack: presence.hasAudioTrack,
      hasVideoTrack: presence.hasVideoTrack,
      sampledAtMs: previous?.sampledAtMs ?? Date.now(),
      subscribeLoss: previous?.subscribeLoss,
      subscribeQuality: previous?.subscribeQuality,
      subscribeRttMs: previous?.subscribeRttMs,
      uid: presence.uid,
      videoBytesReceived: previous?.videoBytesReceived ?? 0,
      videoDecodeFps: previous?.videoDecodeFps,
      videoFramesDecoded: previous?.videoFramesDecoded ?? 0,
      videoFramesReceived: previous?.videoFramesReceived ?? 0,
      videoFramesRendered: previous?.videoFramesRendered ?? 0,
      videoFrameHeight: previous?.videoFrameHeight,
      videoFrameWidth: previous?.videoFrameWidth,
      videoPacketsReceived: previous?.videoPacketsReceived ?? 0,
    };
  }

  private handleRemoteMediaStatsChange(snapshot: RemoteMediaSnapshot): void {
    const previous = this.remoteMediaSnapshot;

    this.remoteMediaSnapshot = {
      audioBytesReceived: mergeSnapshotValue(snapshot.audioBytesReceived, previous?.audioBytesReceived ?? 0, 0),
      audioPacketsReceived: mergeSnapshotValue(snapshot.audioPacketsReceived, previous?.audioPacketsReceived ?? 0, 0),
      hasAudioTrack: mergeSnapshotValue(snapshot.hasAudioTrack, previous?.hasAudioTrack ?? false, false),
      hasVideoTrack: mergeSnapshotValue(snapshot.hasVideoTrack, previous?.hasVideoTrack ?? false, false),
      sampledAtMs: snapshot.sampledAtMs,
      subscribeLoss: snapshot.subscribeLoss ?? previous?.subscribeLoss,
      subscribeQuality: snapshot.subscribeQuality ?? previous?.subscribeQuality,
      subscribeRttMs: snapshot.subscribeRttMs ?? previous?.subscribeRttMs,
      uid: snapshot.uid ?? previous?.uid ?? null,
      videoBytesReceived: mergeSnapshotValue(snapshot.videoBytesReceived, previous?.videoBytesReceived ?? 0, 0),
      videoDecodeFps: snapshot.videoDecodeFps ?? previous?.videoDecodeFps,
      videoFramesDecoded: mergeSnapshotValue(snapshot.videoFramesDecoded, previous?.videoFramesDecoded ?? 0, 0),
      videoFramesReceived: mergeSnapshotValue(snapshot.videoFramesReceived, previous?.videoFramesReceived ?? 0, 0),
      videoFramesRendered: mergeSnapshotValue(snapshot.videoFramesRendered, previous?.videoFramesRendered ?? 0, 0),
      videoFrameHeight: snapshot.videoFrameHeight ?? previous?.videoFrameHeight,
      videoFrameWidth: snapshot.videoFrameWidth ?? previous?.videoFrameWidth,
      videoPacketsReceived: mergeSnapshotValue(snapshot.videoPacketsReceived, previous?.videoPacketsReceived ?? 0, 0),
    };
  }

  private startRemoteMediaStatsTimer(): void {
    if (this.remoteMediaStatsTimer !== null) {
      return;
    }

    let previousSnapshot: RemoteMediaSnapshot | null = null;

    this.remoteMediaStatsTimer = setInterval(() => {
      if (this.exited || this.disposed || !this.remoteMediaSnapshot) {
        return;
      }

      const currentSnapshot = this.remoteMediaSnapshot;
      const previous = previousSnapshot;
      previousSnapshot = { ...currentSnapshot };

      if (!previous) {
        return;
      }

      const currentAudioBytesReceived = currentSnapshot.audioBytesReceived ?? 0;
      const currentAudioPacketsReceived = currentSnapshot.audioPacketsReceived ?? 0;
      const currentVideoBytesReceived = currentSnapshot.videoBytesReceived ?? 0;
      const currentVideoPacketsReceived = currentSnapshot.videoPacketsReceived ?? 0;
      const currentVideoFramesReceived = currentSnapshot.videoFramesReceived ?? 0;
      const currentVideoFramesDecoded = currentSnapshot.videoFramesDecoded ?? 0;
      const currentVideoFramesRendered = currentSnapshot.videoFramesRendered ?? 0;
      const previousAudioBytesReceived = previous.audioBytesReceived ?? 0;
      const previousAudioPacketsReceived = previous.audioPacketsReceived ?? 0;
      const previousVideoBytesReceived = previous.videoBytesReceived ?? 0;
      const previousVideoPacketsReceived = previous.videoPacketsReceived ?? 0;
      const previousVideoFramesReceived = previous.videoFramesReceived ?? 0;
      const previousVideoFramesDecoded = previous.videoFramesDecoded ?? 0;
      const previousVideoFramesRendered = previous.videoFramesRendered ?? 0;
      const hasAudioTrack = currentSnapshot.hasAudioTrack ?? false;
      const hasVideoTrack = currentSnapshot.hasVideoTrack ?? false;
      const audioBytesDelta3s = Math.max(currentAudioBytesReceived - previousAudioBytesReceived, 0);
      const audioPacketsDelta3s = Math.max(currentAudioPacketsReceived - previousAudioPacketsReceived, 0);
      const videoBytesDelta3s = Math.max(currentVideoBytesReceived - previousVideoBytesReceived, 0);
      const videoPacketsDelta3s = Math.max(currentVideoPacketsReceived - previousVideoPacketsReceived, 0);
      const videoFramesReceivedDelta3s = Math.max(currentVideoFramesReceived - previousVideoFramesReceived, 0);
      const videoFramesDecodedDelta3s = Math.max(currentVideoFramesDecoded - previousVideoFramesDecoded, 0);
      const videoFramesRenderedDelta3s = Math.max(currentVideoFramesRendered - previousVideoFramesRendered, 0);

      const audioStatus = this.resolveAudioStatus({
        audioPacketsDelta3s,
        hasAudioTrack,
      });
      const videoStatus = this.resolveVideoStatus({
        hasVideoTrack,
        videoFramesDecodedDelta3s,
        videoPacketsDelta3s,
      });
      const callMediaState = this.resolveCallMediaState({
        audioStatus,
        videoStatus,
      });

      this.lastAudioStatus = audioStatus;
      this.lastVideoStatus = videoStatus;
      this.reconcileAutoPlayBlockedWithAudioFlow({ audioStatus, videoStatus, audioPacketsDelta3s });

      // 状态跳变才双写 DataFinder，回答"中途数据流有没有断"。
      // 高频 3s 快照仍只走 WS media_status；避免事件量爆炸。
      if (callMediaState !== this.lastCallMediaState) {
        const previousState = this.lastCallMediaState;
        this.lastCallMediaState = callMediaState;
        this.logger?.log('media', 'call_media_state_change', {
          state: callMediaState,
          previous_state: previousState ?? undefined,
          audio_status: audioStatus,
          video_status: videoStatus,
          audio_packets_delta_3s: audioPacketsDelta3s,
          video_frames_decoded_delta_3s: videoFramesDecodedDelta3s,
          video_packets_delta_3s: videoPacketsDelta3s,
          has_audio_track: hasAudioTrack,
          has_video_track: hasVideoTrack,
          elapsed_ms_since_session_start: this.logger.since('session_start'),
        });
      }

      this.wsSession?.sendMediaStatus?.({
        payload: {
          audio_bytes_delta_3s: audioBytesDelta3s,
          audio_packets_delta_3s: audioPacketsDelta3s,
          audio_status: audioStatus,
          autoplay_blocked: this.state.autoPlayBlocked,
          call_media_state: callMediaState,
          has_audio_track: hasAudioTrack,
          has_video_track: hasVideoTrack,
          is_call_ready: this.state.isCallReady,
          is_recovering: this.state.isRecovering || this.state.isRtcRecovering,
          is_waiting_for_network: this.state.isWaitingForNetwork,
          remote_uid: currentSnapshot.uid ?? undefined,
          sampled_at_ms: currentSnapshot.sampledAtMs,
          subscribe_loss: currentSnapshot.subscribeLoss,
          subscribe_quality: currentSnapshot.subscribeQuality,
          subscribe_rtt_ms: currentSnapshot.subscribeRttMs,
          video_bytes_delta_3s: videoBytesDelta3s,
          video_decode_fps: currentSnapshot.videoDecodeFps,
          video_frames_decoded_delta_3s: videoFramesDecodedDelta3s,
          video_frames_received_delta_3s: videoFramesReceivedDelta3s,
          video_frames_rendered: currentSnapshot.videoFramesRendered,
          video_frames_rendered_delta_3s: videoFramesRenderedDelta3s,
          video_frame_height: currentSnapshot.videoFrameHeight,
          video_frame_width: currentSnapshot.videoFrameWidth,
          video_packets_delta_3s: videoPacketsDelta3s,
          video_status: videoStatus,
        },
      });
    }, MEDIA_STATUS_REPORT_INTERVAL_MS);
  }

  private clearRemoteMediaStatsTimer(): void {
    if (this.remoteMediaStatsTimer !== null) {
      clearInterval(this.remoteMediaStatsTimer);
      this.remoteMediaStatsTimer = null;
    }
  }

  private resolveAudioStatus({
    audioPacketsDelta3s,
    hasAudioTrack,
  }: {
    audioPacketsDelta3s: number;
    hasAudioTrack: boolean;
  }): LiveTrackStatus {
    if (!hasAudioTrack) {
      return 'no_track';
    }

    if (audioPacketsDelta3s > 0) {
      return 'flowing';
    }

    return 'track_only';
  }

  private resolveVideoStatus({
    hasVideoTrack,
    videoFramesDecodedDelta3s,
    videoPacketsDelta3s,
  }: {
    hasVideoTrack: boolean;
    videoFramesDecodedDelta3s: number;
    videoPacketsDelta3s: number;
  }): LiveTrackStatus {
    if (this.state.isCallReady) {
      return 'rendered';
    }

    if (!hasVideoTrack) {
      return 'no_track';
    }

    if (videoFramesDecodedDelta3s > 0) {
      return 'flowing';
    }

    if (videoPacketsDelta3s > 0) {
      return 'stalled';
    }

    return 'track_only';
  }

  private resolveCallMediaState({
    audioStatus,
    videoStatus,
  }: {
    audioStatus: LiveTrackStatus;
    videoStatus: LiveTrackStatus;
  }): LiveCallMediaState {
    if (this.state.autoPlayBlocked) {
      return 'autoplay_blocked';
    }

    if (this.state.isWaitingForNetwork) {
      return 'waiting_network';
    }

    if (this.state.isRecovering || this.state.isRtcRecovering) {
      return 'reconnecting';
    }

    const audioFlowing = audioStatus === 'flowing';
    const videoFlowing = videoStatus === 'flowing' || videoStatus === 'rendered';

    if (audioFlowing && videoFlowing) {
      return 'normal';
    }

    if (audioFlowing && videoStatus === 'no_track') {
      return 'only_audio';
    }

    if (!audioFlowing && videoFlowing) {
      return 'only_video';
    }

    if (videoStatus === 'stalled') {
      return 'video_stuck';
    }

    if (audioStatus === 'track_only' || videoStatus === 'track_only') {
      return 'no_media';
    }

    return 'connecting';
  }

  private rebindRemoteFrameDetector(): void {
    this.cleanupRemoteFrameDetector();

    const element = this.remoteVideoElement;
    if (!element || this.remoteBindings.length === 0) {
      return;
    }

    let done = false;
    let rvfcHandle: number | null = null;

    const markRendered = () => {
      if (done || this.exited || this.disposed) {
        return;
      }

      done = true;
      this.hasEverRenderedRemoteFrame = true;
      this.hasRemoteFrame = true;
      this.clearConnectTimeout();
      this.clearRemoteMediaLossTimer();
      this.clearRtcRecoveryTimer();

      const isFirstEverConnection = !this.state.hasEverConnected;
      if (isFirstEverConnection) {
        this.callStartedAt = Date.now();
        this.startTickTimer();
      }

      // A rendered frame proves video playback recovered. Audio autoplay can
      // still be blocked, so keep that recovery prompt after the call appears.
      this.autoPlayBlockedKinds.delete('video');
      this.syncAutoPlayBlockedState(true);

      // logOnce 保证 RTC 重建后再次首帧不会重复上报——比依赖 hasEverRenderedRemoteFrame 更稳。
      this.logger?.logOnce('rtc_first_frame', 'rtc', 'rtc_first_frame', {
        elapsed_ms_since_claim_request_start: this.logger.since('claim_request_start'),
        elapsed_ms_since_claim_success: this.logger.since('claim_success'),
        elapsed_ms_since_rtc_join_channel_done: this.logger.since('rtc_join_channel_done'),
        elapsed_ms_since_session_start: this.logger.since('session_start'),
        /*
         * 首屏耗时的两个口径，一条日志里直接可读，不用跨事件拼接：
         * - since_rtc_publish_called：毫秒级精确，起点是「发起推流」，偏乐观
         * - since_local_first_frame_sent：起点是「第一帧真的编码上行」，更贴近真实
         *   服务端往返，但受 rtcLocalVideoStats ~2s 采样间隔影响，最大 ~2s 滞后
         * 两者差值异常大 → 上行卡住；两者都大 → 服务端处理慢。
         * local_first_frame_sent 只留作日志排障对照。
         */
        elapsed_ms_since_rtc_publish_called: this.logger.since('rtc_publish_called'),
        elapsed_ms_since_local_first_frame_sent: this.logger.since('local_first_frame_sent'),
        is_first_ever_connection: isFirstEverConnection,
      });

      this.setState({
        connectionError: null,
        hasEverConnected: true,
        isCallReady: true,
        isRecovering: false,
        isRtcRecovering: false,
        isWaitingForNetwork: false,
      });
    };

    type VideoWithRvfc = HTMLVideoElement & {
      cancelVideoFrameCallback?: (handle: number) => void;
      requestVideoFrameCallback?: (cb: () => void) => number;
    };

    const video = element as VideoWithRvfc;

    if (typeof video.requestVideoFrameCallback === 'function') {
      rvfcHandle = video.requestVideoFrameCallback(markRendered);
    } else {
      element.addEventListener('loadeddata', markRendered);
      element.addEventListener('playing', markRendered);
    }

    this.remoteFrameCleanup = () => {
      if (rvfcHandle !== null && typeof video.cancelVideoFrameCallback === 'function') {
        video.cancelVideoFrameCallback(rvfcHandle);
      }

      element.removeEventListener('loadeddata', markRendered);
      element.removeEventListener('playing', markRendered);
    };
  }

  private cleanupRemoteFrameDetector(): void {
    this.remoteFrameCleanup?.();
    this.remoteFrameCleanup = null;
  }

  private startTickTimer(): void {
    if (this.tickTimer !== null) {
      return;
    }

    const tick = () => {
      if (this.callStartedAt === null) {
        return;
      }

      const elapsedSeconds = Math.floor((Date.now() - this.callStartedAt) / 1000);
      const remainingSeconds = Math.max(this.getMaxDurationSeconds() - elapsedSeconds, 0);

      this.setState({ elapsedSeconds, remainingSeconds });

      if (remainingSeconds <= 0 && !this.exited) {
        void this.exitFromClientActionOrFallback({ reason: 'duration_limit' });
      }
    };

    tick();
    this.tickTimer = setInterval(tick, 1000);
  }

  private clearTickTimer(): void {
    if (this.tickTimer !== null) {
      clearInterval(this.tickTimer);
      this.tickTimer = null;
    }
  }

  private startConnectTimeout(): void {
    this.clearConnectTimeout();

    this.connectTimeoutTimer = setTimeout(() => {
      if (!this.state.isCallReady && !this.state.hasEverConnected) {
        this.logger?.log('call', 'session_connect_timeout', {
          timeout_ms: CONNECT_TIMEOUT_MS,
        });
        this.showConnectionError(this.connectionErrorText, {
          phase: 'connect_timeout',
          source: 'timeout',
        });
        void this.exitFromClientActionOrFallback({ reason: 'connect_timeout' });
      }
    }, CONNECT_TIMEOUT_MS);
  }

  private clearConnectTimeout(): void {
    if (this.connectTimeoutTimer !== null) {
      clearTimeout(this.connectTimeoutTimer);
      this.connectTimeoutTimer = null;
    }
  }

  private scheduleWsReconnect(message?: string): void {
    if (this.exited || this.disposed || !this.state.hasEverConnected) {
      return;
    }

    this.reconnector.schedule(message);
  }

  private scheduleRemoteMediaLossRecovery(): void {
    if (
      this.exited ||
      this.disposed ||
      !this.hasRemoteFrame ||
      this.remoteMediaLossTimer !== null ||
      this.isRebuildingRtc
    ) {
      return;
    }

    this.remoteMediaLossTimer = setTimeout(() => {
      this.remoteMediaLossTimer = null;
      this.scheduleRtcRecovery();
    }, REMOTE_MEDIA_LOSS_GRACE_MS);
  }

  private clearRemoteMediaLossTimer(): void {
    if (this.remoteMediaLossTimer !== null) {
      clearTimeout(this.remoteMediaLossTimer);
      this.remoteMediaLossTimer = null;
    }
  }

  private scheduleRtcRecovery(): void {
    if (!hasNetworkAccess()) {
      if (this.state.hasEverConnected) {
        this.setState({
          connectionError: null,
          isCallReady: this.hasEverRenderedRemoteFrame,
          isRecovering: false,
          isRtcRecovering: true,
        });
      }
      this.handleOffline();
      return;
    }

    if (
      this.exited ||
      this.disposed ||
      !this.state.hasEverConnected ||
      this.state.isWaitingForNetwork ||
      this.isRebuildingRtc ||
      this.rtcRecoveryTimer !== null
    ) {
      return;
    }

    this.clearRemoteMediaLossTimer();
    this.setState({
      connectionError: null,
      isCallReady: this.hasEverRenderedRemoteFrame,
      isRecovering: false,
      isRtcRecovering: true,
    });

    this.logger?.log('call', 'rtc_recovery_scheduled', {
      grace_ms: RTC_RECOVERY_GRACE_MS,
    });

    this.rtcRecoveryTimer = setTimeout(() => {
      this.rtcRecoveryTimer = null;
      void this.rebuildRtcAfterRecoveryGrace();
    }, RTC_RECOVERY_GRACE_MS);
  }

  private clearRtcRecoveryTimer(): void {
    if (this.rtcRecoveryTimer !== null) {
      clearTimeout(this.rtcRecoveryTimer);
      this.rtcRecoveryTimer = null;
    }
  }

  private async rebuildRtcAfterRecoveryGrace(): Promise<void> {
    if (
      this.exited ||
      this.disposed ||
      !this.state.isRtcRecovering ||
      this.state.isWaitingForNetwork ||
      !hasNetworkAccess() ||
      this.isRebuildingRtc
    ) {
      return;
    }

    this.isRebuildingRtc = true;
    this.cleanupRemoteFrameDetector();
    this.clearAutoPlayBlockedState();
    this.setState({
      isCallReady: false,
      isRecovering: true,
      isRtcRecovering: false,
    });

    this.logger?.log('call', 'rtc_rebuild_start');

    try {
      await this.rtcSession.stop();
      this.hasStartedRtc = false;
      this.hasEverRenderedRemoteFrame = false;
      this.hasRemoteFrame = false;
      this.remoteBindings = [];
      this.remoteMediaSnapshot = null;
      await this.startRtcSession();
      this.logger?.log('call', 'rtc_rebuild_done', { success: true });
    } catch (error) {
      this.logger?.log('call', 'rtc_rebuild_done', {
        success: false,
        ...describeError(error, { includeMessage: true }),
      });
      throw error;
    } finally {
      this.isRebuildingRtc = false;
      if (this.retryRtcRebuildAfterFailure) {
        this.retryRtcRebuildAfterFailure = false;
        this.scheduleRtcRecovery();
      }
    }
  }

  private dispatchTextInput(clientMsgId: string, text: string): void {
    const result = this.wsSession?.sendTextInput?.({ clientMsgId, text });

    // WS 未建立 / 未完成握手 / send 抛错：不静默丢，直接置 failed 供重发。
    // socket 写成功只保持 pending —— 由服务端 text_input_ack(accepted) 才翻 sent；
    // 投递失败由服务端 error=15 翻 failed。
    if (!result || !result.sent) {
      this.chatStore.setUserMessageStatus(clientMsgId, 'failed');
    }
  }

  private generateClientMsgId(): string {
    const globalCrypto = typeof crypto !== 'undefined' ? crypto : undefined;
    if (globalCrypto && typeof globalCrypto.randomUUID === 'function') {
      return globalCrypto.randomUUID();
    }
    return `c-${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 10)}`;
  }

  private async handleServerDrivenTerminationSignal(signalType: number | undefined, rawReason?: string): Promise<void> {
    if (signalType === SIGNAL_TYPE_TIMEOUT_HANGUP) {
      this.logger?.log('call', 'session_server_hangup', {
        signal_type: signalType,
        mapped_reason: 'timeout_hangup',
      });
      await this.exitFromServerSignal({ notice: { reason: 'timeout_hangup' } });
      return;
    }

    const mappedReason = resolveLiveExitReasonFromServerCode(rawReason);
    // raw_reason 是服务端 hangup_reason 原文，可能是自定义文案：allowlist 优先，否则脱敏+截断后写入独立字段。
    const rawReasonSanitized = rawReason ? sanitizeString(rawReason) : undefined;

    if (mappedReason && isLiveReconnectOnlyReason(mappedReason)) {
      this.logger?.log('call', 'session_server_hangup', {
        signal_type: signalType,
        mapped_reason: mappedReason,
        raw_reason_sanitized: rawReasonSanitized,
        will_reconnect: this.state.hasEverConnected,
      });
      if (!this.state.hasEverConnected) {
        await this.exitFromServerSignal({ notice: { reason: mappedReason } });
        return;
      }

      this.scheduleWsReconnect(this.appCloseConnNoticeText);
      return;
    }

    this.logger?.log('call', 'session_server_hangup', {
      signal_type: signalType,
      mapped_reason: mappedReason ?? 'server_force_hangup',
      raw_reason_sanitized: rawReasonSanitized,
    });

    await this.exitFromServerSignal({
      notice: mappedReason
        ? { reason: mappedReason }
        : { reason: 'server_force_hangup', message: rawReason || undefined },
    });
  }

  private closeWsSession(sendHangup: boolean, reason?: string): SendHangupResult | null {
    const wsSession = this.wsSession;
    this.wsSession = null;

    if (!sendHangup) {
      wsSession?.dispose();
      return null;
    }

    const result = wsSession?.close({ sendHangup: true, hangupReason: reason }) ?? {
      reason: reason ?? 'unknown',
      sent: false,
      socketState: undefined,
    };

    wsSession?.dispose();

    return result;
  }

  private async disposeTransport(): Promise<void> {
    this.cleanupRemoteFrameDetector();
    this.closeWsSession(false);
    await this.rtcSession.stop();
    this.hasStartedRtc = false;
    this.remoteMediaSnapshot = null;
    this.remoteBindings = [];
    this.hasRemoteFrame = false;
  }

  private async exitWithTransportCleanup({
    notice,
    sendHangup = true,
  }: {
    notice?: LiveExitNotice;
    sendHangup?: boolean;
  } = {}): Promise<void> {
    if (this.exited) {
      return;
    }

    this.exited = true;
    this.detachWindowListeners();
    this.clearConnectTimeout();
    this.clearRemoteMediaStatsTimer();
    this.clearTickTimer();
    this.clearRemoteMediaLossTimer();
    this.clearRtcRecoveryTimer();
    this.reconnector.stop();

    if (!this.lastExitReason) {
      this.lastExitReason = notice?.reason ?? 'unknown';
    }

    // exit reason 用 allowlist 收敛：命中 LiveExitReason 枚举 / 内部主动挂断标识 / route leave 标识才原样上报；
    // 其余归到 unknown 并把原始值放独立字段（已脱敏截断），避免服务端自定义文案泄漏。
    const EXIT_REASON_ALLOWLIST = new Set([
      'user_press_endcall',
      'route_change_leave',
      'pagehide_leave',
      'duration_limit',
      'connect_timeout',
      'not_ready',
      'connection_lost',
      'server_force_hangup',
      'timeout_hangup',
      'audit_rejected',
      'internal_fail',
      'ended',
      'realtime_model_fail',
      'prepared_timeout',
      'user_hangup',
      'app_close_conn',
      'prepared_sip_disconnected',
      'sip_reconnect_timeout',
      'unknown',
    ]);
    const rawExitReason = this.lastExitReason || 'unknown';
    const exitReasonAllowlisted = EXIT_REASON_ALLOWLIST.has(rawExitReason) ? rawExitReason : 'unknown';
    // logOnce 保证同一 session 只上报一次 session_exit：即使 handleRouteLeave 已经 logOnce 过一次，
    // 后续 exitFromServerSignal / exitFromClientActionOrFallback 走到这里也不会重复上报。
    this.logger?.logOnce('session_exit', 'call', 'session_exit', {
      reason: exitReasonAllowlisted,
      reason_raw_sanitized: exitReasonAllowlisted === rawExitReason ? undefined : sanitizeString(rawExitReason),
      notice_reason: notice?.reason,
      has_ever_connected: this.state.hasEverConnected,
      elapsed_ms_since_claim_request_start: this.logger?.since('claim_request_start'),
      elapsed_ms_since_claim_success: this.logger?.since('claim_success'),
      elapsed_ms_since_session_start: this.logger?.since('session_start'),
      send_hangup: sendHangup,
      via: 'transport_cleanup',
    });

    if (sendHangup) {
      const reason = this.lastExitReason || 'unknown';
      this.closeWsSession(true, reason);
    } else {
      this.closeWsSession(false);
    }

    await this.disposeTransport();
    await this.onExit(notice);
  }

  private async exitFromClientActionOrFallback(notice?: LiveExitNotice): Promise<void> {
    await this.exitWithTransportCleanup({ notice, sendHangup: true });
  }

  private async exitFromServerSignal({ notice }: { notice: LiveExitNotice }): Promise<void> {
    await this.exitWithTransportCleanup({ notice, sendHangup: false });
  }

  private attachWindowListeners(): void {
    window.addEventListener('online', this.handleOnlineBound);
    window.addEventListener('offline', this.handleOfflineBound);
    window.addEventListener('focus', this.handleForegroundReconnectBound);
    document.addEventListener('visibilitychange', this.handleForegroundReconnectBound);
  }

  private detachWindowListeners(): void {
    window.removeEventListener('online', this.handleOnlineBound);
    window.removeEventListener('offline', this.handleOfflineBound);
    window.removeEventListener('focus', this.handleForegroundReconnectBound);
    document.removeEventListener('visibilitychange', this.handleForegroundReconnectBound);
  }

  private readonly handleOnlineBound = () => {
    this.handleOnline();
  };

  private readonly handleOfflineBound = () => {
    this.handleOffline();
  };

  private readonly handleForegroundReconnectBound = () => {
    this.handleForegroundReconnect();
  };

  private setState(patch: Partial<LiveCallSessionState>): void {
    this.state = {
      ...this.state,
      ...patch,
    };
    this.emitState();
  }

  private showConnectionError(
    message: string | null | undefined,
    details: {
      error_code?: string;
      phase: string;
      source: ConnectionErrorSource;
    },
  ): void {
    if (!message) {
      return;
    }

    if (!this.state.connectionError) {
      this.logger?.log('call', 'connection_error_shown', {
        error_code: details.error_code,
        has_ever_connected: this.state.hasEverConnected,
        elapsed_ms_since_claim_request_start: this.logger?.since('claim_request_start'),
        elapsed_ms_since_session_start: this.logger?.since('session_start'),
        is_rtc_recovering: this.state.isRtcRecovering,
        is_waiting_for_network: this.state.isWaitingForNetwork,
        phase: details.phase,
        source: details.source,
      });
    }

    this.setState({ connectionError: message });
  }

  private emitState(): void {
    this.onStateChange?.({ ...this.state });
  }

  private getMaxDurationSeconds(): number {
    const serverDuration = this.session.claim.live.live_duration ?? 0;
    return serverDuration > 0 ? serverDuration : DEFAULT_MAX_CALL_DURATION_SECONDS;
  }
}
